/**
 * One playlist: its songs in order, Play and Shuffle, and a ⋯ menu for the rest
 * (add to queue, export, rename, delete). Songs can be moved or removed here.
 * Flow makes playlists (Save as playlist); any song's ⋯ menu adds to one.
 */

import { useEffect, useMemo, useState } from "react";
import type { PlaylistItem } from "../api";
import { useApp } from "../core/app";
import { exportPlaylist } from "../core/commands";
import { useDragReorder } from "../core/dragList";
import { player, usePlayer, type QueueTrack } from "../core/player";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { MoreButton } from "../shell/PopupMenu";
import { toast } from "../shell/Toast";
import { clockTime } from "../shell/Waveform";
import { songs } from "../format/count";

function asQueue(items: PlaylistItem[]): QueueTrack[] {
  return items
    .filter((i) => i.trackId && !i.missing)
    .map((i) => ({
      id: String(i.trackId), title: i.title ?? "Untitled", artist: i.artist ?? "", album: null, bpm: i.bpm,
      musicalKey: i.camelot, lufs: null, cueIn: null, cueOut: null, duration: i.duration ?? 0,
    }));
}

export function PlaylistView({ id }: { id: number }) {
  const app = useApp();
  const p = usePlayer();
  const lib = app.library;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id && lib.playlist?.id !== id) void lib.openPlaylist(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pl = lib.playlist?.id === id ? lib.playlist : null;
  const queue = useMemo(() => (pl ? asQueue(pl.items) : []), [pl]);
  const drag = useDragReorder({
    onMove: (from, to) => {
      if (pl) void act("move", { id: pl.id, itemId: pl.items[from].id, to });
    },
  });

  if (!pl) {
    return (
      <section className="nv-view">
        <div className="nv-empty">{lib.playlists.some((x) => x.id === id) ? "Loading…" : "This playlist no longer exists."}</div>
      </section>
    );
  }

  const act = async (path: string, body: Record<string, unknown>, done?: string) => {
    setBusy(true);
    const error = await lib.playlistAction(path, body);
    setBusy(false);
    if (error) toast(error, "bad");
    else if (done) toast(done, "ok");
  };

  const rename = () => {
    const name = window.prompt("Rename playlist", pl.name)?.trim();
    if (name && name !== pl.name) void act("update", { id: pl.id, name }, `Renamed to “${name}”.`);
  };
  const remove = async () => {
    if (!window.confirm(`Delete the playlist “${pl.name}”?\n\nThe songs stay in your library.`)) return;
    await act("delete", { id: pl.id }, `Deleted “${pl.name}”.`);
    app.navigate("songs");
  };
  const songIndex = (item: PlaylistItem) => queue.findIndex((q) => q.id === String(item.trackId));

  return (
    <section className="nv-view" aria-label={pl.name}>
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>{pl.name}</h1>
          <div className="nv-sub">
            Playlist · {songs(queue.length)} · {clockTime(pl.totalSeconds)}
            {pl.unmeasured > 0 && ` · ${pl.unmeasured} without a measured length`}
          </div>
        </div>
        <div className="nv-actions">
          <label className="nv-inline-toggle" title="Off: it plays exactly as built. On: the clip rules (a station ID every 4 songs, say) play between its songs, wherever it plays, and count any clip you placed in it.">
            <span className="nv-toggle">
              <input type="checkbox" role="switch" disabled={busy} checked={pl.clipRules === true}
                onChange={(e) => void act("update", { id: pl.id, clipRules: e.target.checked },
                  e.target.checked ? "The clip rules will play between its songs." : "It plays exactly as built.")} />
              <i aria-hidden="true" />
            </span>
            <span>Clip rules</span>
          </label>
          <button className="nv-btn primary" disabled={!queue.length} onClick={() => player.playTracks(queue)}>
            <Icon name="play" /> Play
          </button>
          <button className="nv-btn" disabled={!queue.length} onClick={() => { player.setShuffle(true); player.playTracks(queue, Math.floor(Math.random() * queue.length)); }}>
            <Icon name="shuffle" /> Shuffle
          </button>
          <MoreButton
            label="Playlist options"
            items={[
              { id: "queue", group: "a", label: "Add to queue", icon: "queue", disabled: !queue.length, run: () => { player.enqueue(queue); toast(`Added ${songs(queue.length)} to the queue.`, "ok"); } },
              { id: "export", group: "b", label: "Export as M3U…", icon: "download", run: () => exportPlaylist(pl.id) },
              { id: "rename", group: "b", label: "Rename…", icon: "edit", run: rename },
              { id: "delete", group: "c", label: "Delete playlist", icon: "trash", danger: true, run: () => void remove() },
            ]}
          />
        </div>
      </div>
      <div className="nv-gridwrap">
        {!pl.items.length ? (
          <div className="nv-empty">
            <Icon name="list" size={22} />
            <b>This playlist is empty</b>
            <span>Add songs from any song's ⋯ menu → Add to playlist.</span>
          </div>
        ) : (
          <ol className="nv-pl" aria-busy={busy}>
            {pl.items.map((item, i) => {
              const playing = p.current?.track.id === String(item.trackId);
              const row = drag.rowProps(i);
              return (
                <li
                  key={item.id}
                  {...row}
                  title="Drag to move"
                  className={`${playing ? "playing" : ""} ${item.missing ? "missing" : ""} ${row.className}`}
                  onDoubleClick={() => !item.missing && player.playTracks(queue, Math.max(0, songIndex(item)))}
                >
                  <Icon name="grip" size={14} className="nv-grip" />
                  <span className="idx">{playing ? <Icon name="play" size={11} /> : i + 1}</span>
                  <Artwork trackId={item.trackId} seed={item.artist || item.title || ""} size={30} rounded={4} />
                  <div className="nv-pl-text">
                    <b title={item.title ?? undefined}>{item.title ?? "Untitled"}</b>
                    <span>{item.artist}{item.missing ? " · file missing" : ""}</span>
                  </div>
                  <span className="nv-pl-meta">
                    {item.bpm != null ? `${Math.round(item.bpm)} BPM` : ""}
                    {item.camelot ? ` · ${item.camelot}` : ""}
                  </span>
                  <span className="nv-pl-time">{item.duration != null ? clockTime(item.duration) : "—"}</span>
                  <MoreButton
                    label={`Options for ${item.title ?? "item"}`}
                    items={[
                      { id: "play", label: "Play from here", icon: "play", disabled: item.missing, run: () => player.playTracks(queue, Math.max(0, songIndex(item))) },
                      { id: "up", group: "b", label: "Move up", disabled: i === 0, run: () => act("move", { id: pl.id, itemId: item.id, to: i - 1 }) },
                      { id: "down", group: "b", label: "Move down", disabled: i === pl.items.length - 1, run: () => act("move", { id: pl.id, itemId: item.id, to: i + 1 }) },
                      { id: "remove", group: "c", label: "Remove from playlist", icon: "close", danger: true, run: () => act("remove", { id: pl.id, itemIds: [item.id] }) },
                    ]}
                  />
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
